import { useState, useEffect, useContext, useRef } from "react";
import "./WarehouseInventory.css";

import { useLocation } from "react-router-dom";
import Visualizer from "../../components/Visualizer/Index";
import Header from "../../components/Header/Header";
import { round, set } from "lodash";
import { faArrowLeft, faFilter, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Store } from "../../store";

//Constants
import { toast } from "react-toastify";
import { transformElements } from "../../utils/helpers";

//AntDesign import
import { Drawer, Tabs } from "antd";
import Products from "../../components/Filters/Products/Products";
import Categories from "../../components/Filters/Categories/Categories";
import Miscellaneous from "../../components/Filters/Miscellaneous/Miscellaneous";
import { getProducts } from "../../services/inventoryService";
import { getLayoutObjects } from "../../services/layoutObjectsService";
import Loader from "../../components/Loader/Loader";
import { eventEmiterInstance } from "../../utils/EventEmitter";

const WarehouseInventory = () => {
  const location = useLocation();
  const visualizerRef = useRef(null);
  const {
    state: { elements },
    dispatch,
  } = useContext(Store);
  const [setSelectItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [inventoryList, setInventoryList] = useState(null);
  //space Utilization space
  const [spaceUtilization, setSpaceUtilization] = useState(null);
  const [layoutName, setlayoutName] = useState(() => localStorage.getItem("layoutName") || "");

  //Current Active Tab
  const [activeTab, setActiveTab] = useState("1");

  const onOpenDrawer = () => {
    setOpenDrawer(true);
  };

  const onCloseDrawer = () => {
    setOpenDrawer(false);
  };

  const TabConfig = [
    {
      key: "1",
      label: "Products",
      children: <Products vref={visualizerRef} isActive={activeTab === "1"} />,
    },
    {
      key: "2",
      label: "Categories",
      children: <Categories vref={visualizerRef} isActive={activeTab === "2"} />,
    },
    {
      key: "4",
      label: "Misc",
      children: <Miscellaneous vref={visualizerRef} isActive={activeTab === "4"} />,
    },
  ];

  const onTabChangeHandler = (selectedTabKey) => {
    setActiveTab(selectedTabKey);
    // Clear all highlights when locations is empty
    eventEmiterInstance.emit("resetAllRacks", {});
  };

  //GET: Layout Objects
  const fetchLayoutObjects = async () => {
    setLoading(true);
    const { search } = location;
    const queryParams = new URLSearchParams(search);
    const layoutId = queryParams.get("layoutId");
    try {
      const response = await getLayoutObjects(layoutId);
      const transformedElements = transformElements(response);
      // batch dispatch
      dispatch({
        type: "BATCH_DISPATCH",
        payload: [
          { type: "SET_ELEMENTS", payload: transformedElements },
          { type: "SET_INITIAL_LOAD", payload: true },
        ],
      });
    } catch (error) {
      const {
        response: {
          data: { message },
        },
      } = error;
      const defaultErrorMessage = "Oops Something went wrong";
      toast.error(message || defaultErrorMessage);
      console.log("error", error);
      setLoading(false);
    }
  };

  const uniqueEntries = (array, key) => {
    // Early return for invalid inputs
    if (!array?.length) return [];

    // Use Set for O(1) lookups instead of find() which is O(n)
    const seen = new Set();

    return array.filter((item) => {
      const value = item[key];
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
  };

  //Space Utilization calculation in warehouse
  const calculateSpaceUtilization = (inventoryLocations = []) => {
    if (inventoryLocations?.length === 0) return;
    inventoryLocations = uniqueEntries(inventoryLocations, "doc_location");
    const elementMap = { rack: "bins", area: "subArea" };
    //Warehouse Storage Location Codes
    const locationCodes = new Set(
      elements
        .filter(({ objectType }) => objectType in elementMap)
        .flatMap((element) => element[elementMap[element.objectType]])
        .map((item) => item?.code)
        .filter(Boolean)
    );

    //Inventory Locations in Warehouse
    let hasWarehouseInventoryLocations = inventoryLocations?.filter(({ doc_location }) => locationCodes.has(doc_location)) || [];

    let totalOccupiedWarehouseLocations = (hasWarehouseInventoryLocations.length / locationCodes.size) * 100;
    let availableWarehouseLocations = 100 - totalOccupiedWarehouseLocations;

    totalOccupiedWarehouseLocations = round(totalOccupiedWarehouseLocations, 2) || 0;
    availableWarehouseLocations = round(availableWarehouseLocations, 2) || 0;

    setSpaceUtilization({ totalOccupiedWarehouseLocations, availableWarehouseLocations });
  };

  // Get inventory of the current active warehouse to calculate space utilization
  useEffect(() => {
    fetchLayoutObjects().then(() => {
      fetchInventory();
    });
  }, []);

  useEffect(() => {
    calculateSpaceUtilization(inventoryList);
  }, [inventoryList]);

  const fetchInventory = async () => {
    try {
      const { data: inventory } = await getProducts();
      setInventoryList(inventory);
      //return { inventory };
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="warehouse-inventory">
      {elements?.length && (
        <div className="warehouse-inventory-wrapper">
          <Visualizer
            header={<Header backHandler={<FontAwesomeIcon icon={faArrowLeft} className="back-arrow" />} title={layoutName + " Inventory"} />}
            ref={visualizerRef}
            setSelectItem={setSelectItem}
          />

          {/* Inventory  */}
          {/* Filter display toogler */}
          <div className="inventory-filter">
            <div className="inventory-filter-toogler">
              <FontAwesomeIcon icon={faFilter} className="filter-toogler-icon" onClick={onOpenDrawer} />
            </div>
            <Drawer title="Layout Object Finder" onClose={onCloseDrawer} open={openDrawer} width={400} mask={false}>
              <div className="inventory-tabs-wrapper">
                <Tabs defaultActiveKey="1" items={TabConfig} onChange={onTabChangeHandler} />
              </div>
            </Drawer>
          </div>

          <div className="progress-bar-wrapper">
            <h1 className="progress-title">Space Utilization</h1>
            <div className="progress-group">
              <p>Filled Space</p>
              <div className="progress-container">
                <progress className="progress" value={spaceUtilization?.totalOccupiedWarehouseLocations} max={100} />
                <label className="progress-percentage">
                  {spaceUtilization?.availableWarehouseLocations ? (
                    `${spaceUtilization?.totalOccupiedWarehouseLocations}%`
                  ) : (
                    <span>{loading ? <FontAwesomeIcon icon={faCircleNotch} className="loader-icon" /> : "0%"}</span>
                  )}
                </label>
              </div>
            </div>

            <div className="progress-group">
              <p>
                {spaceUtilization?.availableWarehouseLocations ? (
                  `${spaceUtilization?.availableWarehouseLocations}% space available`
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span>{loading ? <FontAwesomeIcon icon={faCircleNotch} className="loader-icon" /> : "0%"}</span>
                    <p>space available</p>
                  </div>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseInventory;
